import axios, {AxiosError, type AxiosResponse, type InternalAxiosRequestConfig} from 'axios';

import {baseURL} from '@/shared/constants';
import {useUserStore} from '@/shared/stores/user.store';


interface CustomRequestConfig extends InternalAxiosRequestConfig {
   _retry?: boolean;
}

const http = axios.create({
   baseURL: baseURL,
   withCredentials: true,
});

// Initial token setup from storage
const USER_TOKEN_STORAGE_KEY = 'u_token';
const storedToken = localStorage.getItem(USER_TOKEN_STORAGE_KEY);
if (storedToken) {
   try {
      const token = JSON.parse(storedToken);
      if (token?.access_token) {
         http.defaults.headers.common['Authorization'] = `Bearer ${token.access_token}`;
      }
   } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse auth token', e);
   }
}

/**
 * REFRESH QUEUE LOGIC
 */
let isRefreshing = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let failedQueue: any[] = [];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const processQueue = (error: any, token: string | null = null) => {
   failedQueue.forEach((prom) => {
      if (error) {
         prom.reject(error);
      } else if (token) {
         prom.resolve(token);
      } else {
         prom.reject(new Error('Failed to refresh token: No token provided'));
      }
   });
   failedQueue = [];
};

/**
 * RESPONSE INTERCEPTOR
 * Handles:
 * 1. Data unwrapping (response.data)
 * 2. 401 Unauthorized token refreshing
 * 3. Request queuing during refresh
 */
http.interceptors.response.use(
   (response: AxiosResponse) => {
      // Return only the data portion to the application
      return response.data;
   },
   async (error: AxiosError) => {
      const originalRequest = error.config as CustomRequestConfig;

      // If not a 401 error, or if we've already retried, reject immediately
      if (error.response?.status !== 401 || originalRequest._retry) {
         return Promise.reject(error);
      }

      // Handle concurrent 401s: queue subsequent requests while the first one refreshes
      if (isRefreshing) {
         return new Promise((resolve, reject) => {
            failedQueue.push({resolve, reject});
         })
            .then((token) => {
               originalRequest.headers['Authorization'] = `Bearer ${token}`;
               return http(originalRequest);
            })
            .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
         /**
          * IMPORTANT: Dynamic import to prevent circular dependency with Pinia/Vue stores.
          */
         const store = useUserStore();

         // Perform the refresh
         const newAccessToken = await store.refreshToken();

         // Update the original request headers
         originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

         processQueue(null, newAccessToken);

         // Retry the original request
         return http(originalRequest);
      } catch (refreshError) {
         processQueue(refreshError, null);
         useUserStore().clearUser();
         window.location.href = '/';
         return Promise.reject(refreshError);
      } finally {
         isRefreshing = false;
      }
   },
);

export default http;
